import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { 
  XMarkIcon, 
  HeartIcon, 
  BanknotesIcon, 
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'

const DONATION_CAUSES = [
  {
    id: 'feeding',
    title: 'School Feeding & Nutrition',
    desc: 'Provides nutritious daily lunches cooked by local mothers for vulnerable pupils.',
    icon: AcademicCapIcon,
    suggestedAmount: '₦15,000 / $20 per child/term',
  },
  {
    id: 'health',
    title: 'Goodwill Medical Centre & Eye Care',
    desc: 'Subsidizes medical consultations, cataract surgeries, and maternal care.',
    icon: HeartIcon,
    suggestedAmount: '₦35,000 / $45 per patient',
  },
  {
    id: 'tba',
    title: 'Traditional Birth Attendants & Maternal Health',
    desc: 'Funds training and clean delivery kits to protect mothers and newborns.',
    icon: SparklesIcon,
    suggestedAmount: '₦50,000 / $65 per kit pack',
  },
]

export default function DonateModal({ isOpen, onClose }) {
  const [copiedField, setCopiedField] = useState(null)
  const [selectedCause, setSelectedCause] = useState('feeding')

  const copyToClipboard = (text, field) => {
    navigator.clipboard?.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2500)
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-2xl border border-gray-100">
                {/* Header Banner */}
                <div className="bg-forest p-6 sm:p-8 text-white relative">
                  <button
                    type="button"
                    className="absolute right-4 top-4 rounded-full p-2 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider mb-3 text-[#8de058]">
                    <HeartIcon className="h-4 w-4" />
                    Support Our Mission
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black font-display tracking-tight">
                    Partner with PACIEH to Transform Lives
                  </h3>
                  <p className="mt-2 text-sm text-white/85 max-w-lg leading-relaxed">
                    Every donation provides direct healthcare, nutrition, and schooling for vulnerable African children and families.
                  </p>
                </div>

                <div className="p-6 space-y-6">
                  {/* Causes selector */}
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
                      Select Impact Area
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {DONATION_CAUSES.map((cause) => {
                        const Icon = cause.icon
                        const isSelected = selectedCause === cause.id
                        return (
                          <button
                            key={cause.id}
                            type="button"
                            onClick={() => setSelectedCause(cause.id)}
                            className={`p-3 rounded-xl border text-left transition-all ${
                              isSelected
                                ? 'border-pacieh-green bg-pacieh-green/5 ring-2 ring-pacieh-green/20'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className={`h-5 w-5 ${isSelected ? 'text-pacieh-green' : 'text-gray-500'}`} />
                              <span className="font-semibold text-xs text-gray-900 leading-tight">
                                {cause.title}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-500 line-clamp-2 mt-1">
                              {cause.desc}
                            </p>
                            <span className="inline-block mt-2 text-[10px] font-medium text-pacieh-blue bg-pacieh-light px-2 py-0.5 rounded">
                              {cause.suggestedAmount}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Bank Details Card */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <BanknotesIcon className="h-5 w-5 text-pacieh-green" />
                        <span className="font-bold text-sm text-gray-900">
                          Direct Bank Transfer (Nigeria / Africa)
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">Official PACIEH Account</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                        <div>
                          <span className="text-[11px] text-gray-500 block">Bank Name</span>
                          <span className="font-semibold text-gray-800">Zenith Bank Plc</span>
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                        <div>
                          <span className="text-[11px] text-gray-500 block">Account Name</span>
                          <span className="font-semibold text-gray-800 text-xs">Pan-African Community Initiative</span>
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center justify-between sm:col-span-2">
                        <div>
                          <span className="text-[11px] text-gray-500 block">Account Number (NGN)</span>
                          <span className="font-mono font-bold text-base text-pacieh-blue">1013759902</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => copyToClipboard('1013759902', 'account')}
                          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-pacieh-green/10 text-pacieh-green hover:bg-pacieh-green hover:text-white transition-colors"
                        >
                          {copiedField === 'account' ? (
                            <span className="flex items-center gap-1">
                              <CheckCircleIcon className="h-4 w-4" /> Copied!
                            </span>
                          ) : (
                            'Copy Number'
                          )}
                        </button>
                      </div>
                    </div>
                    <p className="mt-2 text-[11px] text-gray-500 italic">
                      * Please include your name or target cause as transfer reference (e.g., "Donation - School Feeding").
                    </p>
                  </div>

                  {/* Online Form Action */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <a
                      href="https://forms.gle/jz9bwazyys4EbcmH6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 btn-pill-lime justify-center py-3.5 group"
                    >
                      Fill Online Donation Form
                      <span className="w-5 h-5 rounded-full bg-[#072617] text-white flex items-center justify-center text-xs group-hover:translate-x-0.5 transition-transform">
                        →
                      </span>
                    </a>
                    <a
                      href="https://forms.gle/bss8TuKU6U9xXCZ89"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-gray-300 text-gray-800 font-bold hover:bg-gray-50 transition-all text-sm"
                    >
                      Join as Volunteer
                    </a>
                  </div>
                </div>

                {/* Footer Note */}
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <span>Registered Non-Profit Organization in Nigeria</span>
                  <button
                    type="button"
                    onClick={onClose}
                    className="font-medium text-gray-600 hover:text-gray-900"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
